import { createContext, useContext } from 'react';

import type { CommandPaletteError } from '../command-palette-error';
import type { CommandPaletteEvent } from '../types/command-palette-events';

/**
 * Telemetry hooks for the palette: the analytics event sink and the
 * error sink. These are palette-wide concerns provided by the
 * consumer at `<CommandPaletteProvider onEvent={...} onError={...} />`.
 *
 * They live in their own context (rather than being attached to the
 * registry context) because they're conceptually unrelated to
 * registry data: state transitions, command executions, and
 * registration errors all need to report out.
 *
 * The context value is set once at provider mount from props. The
 * provider preserves identity across renders, so any consumer can
 * memoise on it without churn.
 */
export interface CommandPaletteTelemetryContextValue {
  onEvent: (event: CommandPaletteEvent) => void;
  onError: (error: CommandPaletteError) => void;
}

export const CommandPaletteTelemetryContext = createContext<CommandPaletteTelemetryContextValue | null>(null);

/**
 * Read the telemetry hooks. Throws if used outside a provider
 * (matches the convention of the other internal hooks).
 */
export function useCommandPaletteTelemetry(): CommandPaletteTelemetryContextValue {
  const value = useContext(CommandPaletteTelemetryContext);
  if (value === null) {
    throw new Error(
      'useCommandPaletteTelemetry must be used within a <CommandPaletteProvider>. ' +
        'Wrap your tree with <CommandPaletteProvider>.',
    );
  }
  return value;
}
